import { IndividualType } from '@/hooks/useIndividuals'

import { useRegenRealtime } from './useRegenRealtime'
import { useTriggerRegen } from './useTriggerRegen'

export type RegenState = {
  stale: boolean
  isRunning: boolean
  isError: boolean
  error?: string
  regenerate: () => void
}

/**
 * Single source of regen UI state: merges live RTDB state over the individual
 * response and exposes a trigger. Called once by RegenProvider so the whole app
 * shares one RTDB subscription — consumers read it via `useRegen()`.
 *
 * `individual` is optional because the provider mounts before an individual is
 * selected; without one this returns inert state and no subscription is opened.
 */
export const useRegenState = (individual?: IndividualType): RegenState => {
  const live = useRegenRealtime(individual?.id)
  const { isPending, mutateAsync } = useTriggerRegen()

  // individual.regen is absent on older individuals created before the field
  // existed, despite the generated type marking it required. Fall back safely.
  const status = live?.status ?? individual?.regen?.status ?? 'idle'
  const stale = live?.stale ?? individual?.regen?.stale ?? false

  // The error message is only on the individual response (RTDB mirrors status +
  // stale only). When a run fails, the RTDB listener invalidates the individual
  // query, so the message lands on the next render.
  const error = status === 'error' ? individual?.regen?.error : undefined

  const regenerate = () => {
    if (!individual) return
    void mutateAsync({ individualId: individual.id })
  }

  return {
    stale,
    isRunning: status === 'running' || isPending,
    isError: status === 'error',
    error,
    regenerate,
  }
}
